/** Review & run — the dependency-ordered board over the whole checklist.
 *
 *  "Run setup" dispatches every eligible item server-side (run_ready) and arms
 *  the auto-advance sweep, so completions cascade with the page closed; the
 *  board is a live monitor (the shared 4s items poll), not an orchestrator.
 *  Rows link their blockers, expose the run log, offer a retry for errored
 *  machine-runnable items, and inline a TaskProgressCard while a data-sync
 *  item's background task runs.
 */
import HistoryIcon from '@mui/icons-material/History';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { OnboardingProfile, PreflightItem } from '@/api/preflight';
import { AssignedGroupLink } from '@/features/preflight/AssignedGroupLink';
import { Glyph, GlyphLegend, itemGlyph } from '@/features/preflight/glyphs';
import { BlockedChip } from '@/features/preflight/PreflightBlockers';
import { TaskProgressCard } from '@/features/bgtasks/TaskProgressCard';

import { PreflightItemRunLogDrawer } from '../../tabs/PreflightRunLog';
import { taskIdOf } from '../SetupItemPanel';
import { LaunchStateCard } from './LaunchStateCard';

interface Props {
  topId: number;
  items: PreflightItem[];
  profile: OnboardingProfile | undefined;
  canManage: boolean;
  runReady: (itemIds?: number[]) => void;
  runReadyPending: boolean;
  setAutoAdvance: (enabled: boolean) => void;
}

export function RunBoardStep({
  topId,
  items,
  profile,
  canManage,
  runReady,
  runReadyPending,
  setAutoAdvance,
}: Props) {
  const [logItem, setLogItem] = useState<PreflightItem | null>(null);

  const applicable = items.filter((i) => i.applicable);
  const naCount = items.length - applicable.length;
  const done = applicable.filter((i) => i.status === 'completed');
  const running = applicable.filter((i) => i.status === 'started');
  const errored = applicable.filter((i) => i.status === 'error');
  const requiredOpen = applicable.filter((i) => !i.optional && i.status !== 'completed');
  const allDone = applicable.length > 0 && requiredOpen.length === 0;

  const setup = profile?.setup ?? {};
  const armed = Array.isArray(setup.run_actions) && setup.run_actions.length > 0;
  const glyphsInUse = [...new Set(applicable.map(itemGlyph))];

  // Group by group_name, applicable only, arrival order (backend orders by group).
  const groups: { name: string; items: PreflightItem[] }[] = [];
  for (const item of applicable) {
    const name = item.group_name || item.group_code;
    const bucket = groups.find((g) => g.name === name);
    if (bucket) bucket.items.push(item);
    else groups.push({ name, items: [item] });
  }

  // Nothing applies to this operator — the generic "Run setup starts everything"
  // banner over an empty board says the opposite of what is true.
  if (applicable.length === 0) {
    return (
      <Alert severity="info">
        Nothing to run — none of the {items.length} checklist items apply to this operator’s
        profile. Change an answer on the{' '}
        <Link component={RouterLink} to={`/tour-operators/${topId}/setup/profile`}>
          Profile step
        </Link>{' '}
        if that looks wrong.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {allDone ? (
        <Alert severity="success">
          Setup is complete — every applicable item is done
          {applicable.some((i) => i.optional && i.status !== 'completed')
            ? ' (optional items can still be finished below)'
            : ''}
          . <Link component={RouterLink} to={`/tour-operators/${topId}`}>Open the tenant</Link> or
          review the{' '}
          <Link component={RouterLink} to={`/tour-operators/${topId}/preflight`}>
            Preflight tab
          </Link>
          .
        </Alert>
      ) : (
        <Alert severity="info">
          <b>Run setup</b> starts everything that is ready and arms the server-side sweep, so items
          run as their prerequisites complete — you can leave this page and come back any time.
          Human-action items stay with their owners.
        </Alert>
      )}

      {/* The go-live switch, visible: policy / restech sends / notification state together. */}
      {profile?.launch_state && (
        <LaunchStateCard topId={topId} state={profile.launch_state} canManage={canManage} />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" color="success" variant={done.length ? 'filled' : 'outlined'} label={`Done: ${done.length}`} />
        <Chip size="small" color="warning" variant={running.length ? 'filled' : 'outlined'} label={`Running: ${running.length}`} />
        <Chip size="small" color="error" variant={errored.length ? 'filled' : 'outlined'} label={`Errors: ${errored.length}`} />
        <Chip size="small" variant="outlined" label={`Open: ${applicable.length - done.length}`} />
        {naCount > 0 && <Chip size="small" variant="outlined" label={`N/A: ${naCount}`} />}
        <Box sx={{ flexGrow: 1 }} />
        <GlyphLegend kinds={glyphsInUse} />
        {canManage && armed && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={setup.auto_advance !== false}
                onChange={(e) => setAutoAdvance(e.target.checked)}
              />
            }
            label={<Typography variant="caption">Auto-advance</Typography>}
          />
        )}
      </Box>

      {groups.map((group) => (
        <Box key={group.name}>
          <Typography
            variant="overline"
            sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}
          >
            {group.name}
          </Typography>
          <Stack spacing={0.5}>
            {group.items.map((item) => {
              const taskId = item.status === 'started' ? taskIdOf(item) : null;
              return (
                <Box key={item.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Glyph kind={itemGlyph(item)} />
                    <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                      {item.title}
                      {item.optional && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          (opt)
                        </Typography>
                      )}
                    </Typography>
                    {item.status !== 'completed' && <BlockedChip blockers={item.blocked_by} />}
                    <AssignedGroupLink item={item} />
                    {canManage && item.status === 'error' && (
                      <Tooltip title="Retry (re-dispatch through the setup run)">
                        <span>
                          <IconButton
                            size="small"
                            disabled={runReadyPending}
                            onClick={() => runReady([item.id])}
                            aria-label="Retry"
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="View log">
                      <IconButton size="small" onClick={() => setLogItem(item)} aria-label="View log">
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {item.status === 'error' && item.error_message && (
                    <Typography variant="caption" color="error.main" sx={{ pl: 4, display: 'block' }}>
                      {item.error_message}
                    </Typography>
                  )}
                  {taskId && (
                    <Box sx={{ pl: 4, pr: 1, pb: 0.5 }}>
                      <TaskProgressCard taskId={taskId} dense />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}

      <PreflightItemRunLogDrawer
        topId={topId}
        itemId={logItem?.id ?? null}
        title={logItem?.title}
        open={logItem != null}
        onClose={() => setLogItem(null)}
      />
    </Stack>
  );
}
